// src/serviceWorkerRegistration.js
// Uses firebase from node_modules to avoid bundler ESM-from-URL issues.
// Replace FIREBASE_CONFIG and VAPID_KEY with your values.

import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { api } from "./axios"; // keep if you want to send token to backend
import { toast } from "react-toastify";

const FIREBASE_CONFIG = {
  apiKey: "AIzaSyADPv-jxoJAZK87jZA286McKqaByS0hPCk",
  authDomain: "memiz-bd3de.firebaseapp.com",
  projectId: "memiz-bd3de",
  storageBucket: "memiz-bd3de.firebasestorage.app",
  messagingSenderId: "428587719808",
  appId: "1:428587719808:web:59974dd797ce0682565a65",
  measurementId: "G-NVXDRPJTBP",
};

const VAPID_KEY =
  "BC3rc05aKGfy8ZYFNG-I8b507CP58hKjDyw8Q3Ki8YbwGzsYY8rEhnUrgqd23CqoEI4ZNH96-C8Q1C3gIr7VmvY";

// Initialize firebase app lazily (only when needed)
let firebaseApp = null;
function initFirebaseOnce() {
  if (!firebaseApp) firebaseApp = initializeApp(FIREBASE_CONFIG);
  return firebaseApp;
}

/**
 * Try to get FCM token if permission is granted.
 * Requires a service worker registration (so pass the registration you got).
 */
async function tryGetFcmToken(registration) {
  try {
    const app = initFirebaseOnce();
    const messaging = getMessaging(app);

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (!token) {
      console.warn("No FCM token received. Check VAPID key and permissions.");
      return null;
    }

    console.log("FCM token obtained:", token);
    localStorage.setItem("fcmToken", token);

    // Optionally send to backend:
    // try {
    //   // adjust endpoint/payload as your backend expects
    //   await api.post("/fcm-tokens", { token });
    // } catch (e) {
    //   console.warn("Sending FCM token to backend failed:", e);
    // }

    // Foreground message handler
    onMessage(messaging, (payload) => {
      console.log("Foreground message:", payload);
      const title =
        payload.notification?.title ||
        payload.data?.title ||
        "New notification";
      const body = payload.notification?.body || payload.data?.body || "";
      if (Notification.permission === "granted") {
        // show simple notification; click handling is typically done in SW
        new Notification(title, {
          body,
          actions: [
            { action: "open", title: "Open" },
            { action: "dismiss", title: "Dismiss" },
          ],
        });
      }
    });

    return token;
  } catch (err) {
    console.error("FCM token request failed:", err);
    return null;
  }
}

/**
 * Id of the toast currently reporting install progress, or null if none is
 * showing. Shared between the worker statechange handler below and the
 * postMessage handler further down, since both feed the same one toast.
 */
let installToastId = null;

function ensureInstallToast(initialMessage) {
  if (installToastId === null) {
    installToastId = toast.loading(initialMessage);
  }
  return installToastId;
}

/**
 * Show a toast that tracks an installing worker through its lifecycle
 * (installing -> installed -> activating -> activated). Purely a UI concern —
 * does not touch how the service worker itself installs or caches anything.
 * The real 0-100% while "installing" comes from SW_PRECACHE_PROGRESS messages
 * handled below; this only covers the states around that (start + finish).
 */
function trackWorkerProgress(worker) {
  if (!worker) return;

  const handleStateChange = () => {
    switch (worker.state) {
      case "installing":
        ensureInstallToast("Downloading offline files… 0%");
        break;
      case "installed":
        if (installToastId !== null) {
          toast.update(installToastId, { render: "Finishing setup…", progress: null });
        }
        break;
      case "activating":
        if (installToastId !== null) {
          toast.update(installToastId, { render: "Almost ready…" });
        }
        break;
      case "activated":
        if (installToastId !== null) {
          toast.update(installToastId, {
            render: "Memiz is ready to go",
            type: "success",
            isLoading: false,
            progress: undefined,
            autoClose: 2000,
          });
          installToastId = null;
        }
        break;
      default:
        break;
    }
  };

  worker.addEventListener("statechange", handleStateChange);
  // Run once immediately in case the worker is already mid-state
  // (e.g. "installing") by the time we attach the listener.
  handleStateChange();
}

/**
 * Watches a registration for the current install (if one is already in
 * progress) and any future update found during this session.
 */
function trackInstallProgress(registration) {
  if (!registration) return;
  trackWorkerProgress(registration.installing);
  registration.addEventListener("updatefound", () => {
    trackWorkerProgress(registration.installing);
  });
}

// The service worker posts SW_PRECACHE_PROGRESS/SW_PRECACHE_ERROR messages to
// every open tab while it precaches files during install (see src-sw.js). This
// is what turns the toast above into a real percentage rather than just stages.
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.addEventListener("message", (event) => {
    const data = event.data;
    if (!data) return;

    if (data.type === "SW_PRECACHE_PROGRESS") {
      const id = ensureInstallToast(`Downloading offline files… ${data.percent}%`);
      toast.update(id, {
        render: `Downloading offline files… ${data.percent}%`,
        progress: data.percent / 100,
      });
    }

    if (data.type === "SW_PRECACHE_ERROR" && installToastId !== null) {
      toast.update(installToastId, {
        render: "Couldn't finish offline setup — will retry next visit",
        type: "error",
        isLoading: false,
        progress: undefined,
        autoClose: 4000,
      });
      installToastId = null;
    }
  });
}

/**
 * Register service worker always (except localhost block if desired).
 * Will still request notification permission and attempt token only if granted.
 */
export async function register() {
  if (!("serviceWorker" in navigator)) {
    console.log("No service worker support in this browser");
    return;
  }

  // DEV: allow localhost for testing. Set to true in dev if you want to skip
  const allowLocal = process.env.NODE_ENV === "production";
  if (
    (!allowLocal && window.location.hostname === "localhost") ||
    window.location.hostname === "127.0.0.1"
  ) {
    console.log("Skipping SW registration on localhost per config");
    return;
  }

  window.addEventListener("load", async () => {
    let registration = null;
    try {
      // 1) Register service worker (output: /service-worker.js)
      registration = await navigator.serviceWorker.register("/service-worker.js");
      console.log("Service Worker registered:", registration);

      // 1b) Show install-progress feedback (UI-only, no effect on caching)
      trackInstallProgress(registration);

      // 2) Ensure SW is ready
      await navigator.serviceWorker.ready;

      // 3) Request permission (show popup) — but DO NOT bail out if denied.
      // We want the SW registered even if notifications are denied.
      try {
        const permission = await Notification.requestPermission();
        console.log("Notification permission:", permission);

        if (permission === "granted") {
          // Only initialize messaging & get token if permission granted
          await tryGetFcmToken(registration);
        } else {
          // Permission denied or default — continue without messaging
          console.warn("Notifications not granted. SW still registered.");
        }
      } catch (permErr) {
        console.warn("Notification.requestPermission() failed:", permErr);
      }

      // 4) tell SW to skip waiting (optional)
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: "SKIP_WAITING" });
      }
    } catch (err) {
      console.error("Service Worker registration failed:", err);
    }
  });
}

/**
 * Exposed helper so you can trigger permission+token flow later
 * (e.g. user clicks "Enable notifications" in settings).
 * Will re-request permission and try to obtain/send token.
 */
export async function requestAndRegisterToken() {
  if (!("serviceWorker" in navigator)) {
    console.warn("No service worker support in this browser");
    return null;
  }

  try {
    // if SW isn't registered yet, register it now
    let registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      registration = await navigator.serviceWorker.register("/service-worker.js");
      console.log("Service Worker registered (requestAndRegisterToken):", registration);
      await navigator.serviceWorker.ready;
    }

    const permission = await Notification.requestPermission();
    console.log("Notification permission (manual request):", permission);

    if (permission === "granted") {
      return await tryGetFcmToken(registration);
    } else {
      console.warn("User did not grant notifications.");
      return null;
    }
  } catch (err) {
    console.error("requestAndRegisterToken failed:", err);
    return null;
  }
}
